import { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import './PDFViewer.css';

// 设置 PDF.js worker 路径
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export default function PDFViewer({ file }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(0.7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [jumpPageInput, setJumpPageInput] = useState('');
  const [contentPadding, setContentPadding] = useState(0);
  const containerRef = useRef(null);
  const toolbarRef = useRef(null);
  const isMobileRef = useRef(false);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
    setLoading(false);
    setError(null);
    updateContentPadding();
  }

  function onDocumentLoadError(error) {
    console.error(error);
    setError('无法加载PDF文件，请重试');
    setLoading(false);
  }

  function changePage(offset) {
    const newPage = Math.max(1, Math.min(pageNumber + offset, numPages));
    setPageNumber(newPage);
    // 滚动到左上角确保内容可见
    if (containerRef.current) {
      containerRef.current.scrollTo(0, 0);
    }
  }

  function jumpToPage() {
    const page = parseInt(jumpPageInput, 10);
    if (!isNaN(page) && page >= 1 && page <= numPages) {
      setPageNumber(page);
      // 滚动到左上角确保内容可见
      if (containerRef.current) {
        containerRef.current.scrollTo(0, 0);
      }
    } else {
      setJumpPageInput('');
    }
  }

  function handlePageInputKeyDown(e) {
    if (e.key === 'Enter') {
      jumpToPage();
    }
  }

  function handleZoom(direction) {
    const newScale = direction === 'in' ? scale + 0.1 : scale - 0.1;
    const clampedScale = Math.max(0.5, Math.min(newScale, 3.0));
    setScale(clampedScale);
    setTimeout(updateContentPadding, 100);
  }

  // 更新内容区域底部内边距
  function updateContentPadding() {
    if (toolbarRef.current && isMobileRef.current) {
      const toolbarHeight = toolbarRef.current.offsetHeight;
      setContentPadding(toolbarHeight + 20); // 工具栏高度 + 额外边距
    } else {
      setContentPadding(0);
    }
  }
  useEffect(() => {
    setLoading(true);
    setError(null);
    function checkIsMobile() {
      isMobileRef.current = window.innerWidth <= 480;
      updateContentPadding();
    }
    // 初始检测
    checkIsMobile();
    
    // 添加事件监听器
    window.addEventListener('resize', checkIsMobile);
    
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, [file]);

  return (
    <div className="pdf-viewer-container">
      <div 
        className="document-container" 
        ref={containerRef}
        style={{ paddingBottom: `${contentPadding}px` }}
      >
        {loading && (
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>正在加载文档...</p>
          </div>
        )}
        
        {error && (
          <div className="error-message">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <p>{error}</p>
          </div>
        )}
        
        <div className="pdf-content-wrapper">
          <Document
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={null}
          >
            <Page 
              pageNumber={pageNumber} 
              scale={scale}
              renderAnnotationLayer={false}
              className="pdf-page"
            />
          </Document>
        </div>
      </div>
      
      <div className="toolbar" ref={toolbarRef}>
        <div className="nav-controls">
          <button 
            onClick={() => changePage(-1)} 
            disabled={pageNumber <= 1}
            className="nav-button"
            aria-label="上一页"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
            </svg>
          </button>
          
          <div className="page-indicator">
            <span>第 {pageNumber} / {numPages || '--'} 页</span>
          </div>
          
          <button 
            onClick={() => changePage(1)} 
            disabled={pageNumber >= numPages}
            className="nav-button"
            aria-label="下一页"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
            </svg>
          </button>
        </div>
        
        <div className="jump-controls">
          <input
            type="number"
            value={jumpPageInput}
            onChange={(e) => setJumpPageInput(e.target.value)}
            onKeyDown={handlePageInputKeyDown}
            placeholder="页码"
            min="1"
            max={numPages || undefined}
            aria-label="跳转到页码"
          />
          <button 
            onClick={jumpToPage}
            className="jump-button"
            disabled={!jumpPageInput}
          >
            跳转
          </button>
        </div>
        
        <div className="zoom-controls">
          <button 
            onClick={() => handleZoom('out')}
            className="zoom-button"
            disabled={scale <= 0.5}
            aria-label="缩小"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13H5v-2h14v2z" />
            </svg>
          </button>
          <span className="zoom-percent">{(scale * 100).toFixed(0)}%</span>
          <button 
            onClick={() => handleZoom('in')} 
            className="zoom-button"
            disabled={scale >= 3.0}
            aria-label="放大"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}